import React from 'react';
import type { ExpiringPriorAuthTableRow } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { SearchHighlightText } from '../SearchHighlightText/SearchHighlightText';
import { PreviewDateWithStatus } from '../Table';

interface ExpiringPriorAuthPreviewItemProps {
  expiringPriorAuth: ExpiringPriorAuthTableRow;
  searchTerm: string;
}

/**
 * Preview item component for expiring prior auth search results.
 * Highlights fields that match the search term (drug, patient name, DOB).
 */
export const ExpiringPriorAuthPreviewItem: React.FC<ExpiringPriorAuthPreviewItemProps> = ({
  expiringPriorAuth,
  searchTerm,
}) => {
  const formattedDob = expiringPriorAuth.formatted_dob;
  const formattedExpiresDate = formatStandardDate(expiringPriorAuth.formatted_approval_until);
  const isDismissed = expiringPriorAuth.pa.dismissed;

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Typography styledAs="bodySmallDMSans" renderedAs="div" weight={600} color="var(--grayscale-900)">
          <SearchHighlightText searchTerm={searchTerm} value={expiringPriorAuth.drug_name} fieldType="text" truncate />
        </Typography>

        <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--grayscale-900)">
          Expires {formattedExpiresDate}
        </Typography>

        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            color="var(--grayscale-900)"
            className="max-w-[200px] shrink-0"
          >
            <SearchHighlightText
              searchTerm={searchTerm}
              value={expiringPriorAuth.rx.patient_name}
              fieldType="name"
              truncate
            />
          </Typography>
          {formattedDob && (
            <Typography styledAs="bodySmallDMSans" renderedAs="span" color="var(--grayscale-600)">
              (DOB: <SearchHighlightText searchTerm={searchTerm} value={formattedDob} fieldType="date" />)
            </Typography>
          )}
        </div>
      </div>

      <PreviewDateWithStatus
        date={expiringPriorAuth.formatted_prescription_recieved_at}
        statusLabel={isDismissed ? 'Dismissed' : undefined}
      />
    </div>
  );
};

export default ExpiringPriorAuthPreviewItem;
